// src/components/infografia/modals/ImageLibraryModal.jsx
import React, { useState, useEffect } from 'react';
import { FaTimes, FaSearch, FaUpload, FaSpinner } from 'react-icons/fa';
import infografiaService from '../../../helpers/infografia/infografiaService';
import Swal from 'sweetalert2';

const ImageLibraryModal = ({ isOpen, onClose, onSelectImage, marcaId, onUploadNew }) => {
    const [imagenes, setImagenes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            loadImagenes();
        }
    }, [isOpen, marcaId, page, searchTerm]);

    const loadImagenes = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await infografiaService.getImagenesUsuario(marcaId, {
                page,
                limit: 20,
                search: searchTerm || undefined
            });

            const data = response?.imagenes || response || [];
            setImagenes(Array.isArray(data) ? data : []);
            setTotalPages(response?.pagination?.totalPages || 1);
        } catch (error) {
            console.error('Error cargando imágenes:', error);
            setError('No se pudieron cargar las imágenes. El endpoint puede no estar implementado aún.');
            setImagenes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectImage = (imagen) => {
        onSelectImage({
            cdnUrl: imagen.cdnUrl || imagen.url,
            url: imagen.url || imagen.cdnUrl,
            key: imagen.key,
            originalName: imagen.originalName
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Biblioteca de Imágenes
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <FaTimes className="w-5 h-5" />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="p-4 border-b border-gray-200">
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar por nombre de archivo..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPage(1); // Reset to first page on search
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        {onUploadNew && (
                            <button
                                onClick={onUploadNew}
                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
                            >
                                <FaUpload />
                                Subir Nueva
                            </button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <FaSpinner className="w-8 h-8 text-blue-500 animate-spin" />
                            <span className="ml-3 text-gray-600">Cargando imágenes...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <p className="text-red-500 mb-4">{error}</p>
                            <p className="text-sm text-gray-500">
                                Esta funcionalidad requiere que el backend implemente el endpoint GET /api/infografias/imagenes
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                                Ver documentación en: docs/infografia/BACKEND_IMAGENES_USUARIO.md
                            </p>
                        </div>
                    ) : imagenes.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500 mb-4">No hay imágenes disponibles</p>
                            {onUploadNew && (
                                <button
                                    onClick={onUploadNew}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                >
                                    Subir tu primera imagen
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {imagenes.map((imagen) => (
                                <div
                                    key={imagen.id || imagen._id || imagen.key}
                                    onClick={() => handleSelectImage(imagen)}
                                    className="relative group cursor-pointer border border-gray-200 rounded-lg overflow-hidden hover:border-blue-500 hover:shadow-lg transition-all"
                                >
                                    <div className="aspect-square bg-gray-100 relative">
                                        <img
                                            src={imagen.cdnUrl || imagen.url}
                                            alt={imagen.originalName || 'Imagen'}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <div className="hidden absolute inset-0 items-center justify-center bg-gray-200">
                                            <span className="text-gray-400 text-xs">Error al cargar</span>
                                        </div>
                                    </div>
                                    <div className="p-2 bg-white">
                                        <p className="text-xs text-gray-600 truncate" title={imagen.originalName}>
                                            {imagen.originalName || 'Sin nombre'}
                                        </p>
                                        {imagen.dimensiones && (
                                            <p className="text-xs text-gray-400">
                                                {imagen.dimensiones.ancho} × {imagen.dimensiones.alto}px
                                            </p>
                                        )}
                                    </div>
                                    <div className="absolute inset-0 bg-blue-500 bg-opacity-0 group-hover:bg-opacity-10 transition-opacity flex items-center justify-center">
                                        <span className="text-white font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                                            Seleccionar
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between p-4 border-t border-gray-200">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Anterior
                        </button>
                        <span className="text-sm text-gray-600">
                            Página {page} de {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Siguiente
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageLibraryModal;

